import { trace } from '@opentelemetry/api';

import {
  classifyFetchFailure,
  isRetriableTransportCause,
} from '@/utils/classifyFetchFailure';

import { logError } from './logger.mjs';
import getCacheStrategy from './server/getCacheStrategy';

const extractOpNameFromQuery = q => {
  if (!q || typeof q !== 'string') return null;
  const m = q.trim().match(/^(?:query|mutation|subscription)\s+(\w+)/);
  return m ? m[1] : null;
};

/**
 * Metadata for one GraphQL document object (not a JSON batch array).
 */
const metaFromSingleGraphqlDocument = (doc, method, url) => {
  const queryText = doc.query || '';
  const trimmed = queryText.trimStart();
  const isMutation = trimmed.startsWith('mutation');
  const isQuery = trimmed.startsWith('query');
  const isSubscription = trimmed.startsWith('subscription');
  const operationName = doc.operationName || extractOpNameFromQuery(queryText);

  let debugLabel = `${method} ${url}`;
  if (isQuery && operationName) debugLabel = `query ${operationName}`;
  else if (isMutation && operationName)
    debugLabel = `mutation ${operationName}`;

  const operationTag = operationName
    ? String(operationName)
    : isQuery
    ? 'anonymous_query'
    : isMutation
    ? 'anonymous_mutation'
    : isSubscription
    ? 'subscription'
    : 'unknown';

  const isQueryOnly = isQuery && !isMutation && !isSubscription;

  return {
    operationTag,
    debugLabel,
    isQueryOnly,
  };
};

/**
 * Parses GraphQL POST body for Sentry tags, debug labels, and retry safety.
 * Apollo BatchHttpLink with batchMax: 1 often sends a one-element JSON array;
 * that case is treated like a single document so query retries still apply.
 */
const parseGraphqlFetchBody = (url, options) => {
  const method = options?.method || 'GET';
  const body = options?.body;

  if (!body || typeof body !== 'string') {
    return {
      operationTag: 'unknown',
      debugLabel: `${method} ${url}`,
      isQueryOnly: false,
    };
  }

  try {
    const parsed = JSON.parse(body);

    if (Array.isArray(parsed)) {
      if (
        parsed.length === 1 &&
        parsed[0] != null &&
        typeof parsed[0] === 'object'
      ) {
        return metaFromSingleGraphqlDocument(parsed[0], method, url);
      }

      const names = parsed
        .map(p => p?.operationName || extractOpNameFromQuery(p?.query))
        .filter(Boolean);
      const unique = [...new Set(names)];
      const preview = unique.slice(0, 3).join(',');
      const opTag =
        unique.length === 0
          ? 'batch'
          : unique.length > 3
          ? `${preview}+`
          : preview;
      return {
        operationTag: opTag,
        debugLabel: `batch(${opTag})`,
        isQueryOnly: false,
      };
    }

    return metaFromSingleGraphqlDocument(parsed, method, url);
  } catch {
    return {
      operationTag: 'unknown',
      debugLabel: `${method} ${url}`,
      isQueryOnly: false,
    };
  }
};

const logFetchFailure = (error, url, causeErr, bodyMeta, attemptMeta = {}) => {
  const { operationTag, debugLabel } = bodyMeta;
  const {
    fetchFailureClass,
    causeCode,
    causeChainSummary,
  } = classifyFetchFailure(causeErr);

  logError(
    error,
    {
      graphql_operation: operationTag,
      fetchFailureClass,
    },
    {
      fetch_debug_label: debugLabel,
      fetch_url: String(url),
      underlying_error: causeErr?.name,
      underlying_message: causeErr?.message,
      causeCode: causeCode || undefined,
      causeChainSummary,
      ...attemptMeta,
    }
  );
};

export const fetchWithRetry = async (url, options) => {
  const bodyMeta = parseGraphqlFetchBody(url, options);
  const { debugLabel, isQueryOnly, operationTag } = bodyMeta;

  // Offline mock playback (`yarn dev-mock`). The flag is checked inline here —
  // rather than inside tryMockGraphQLResponse — for two reasons:
  //   1. Readability: a reader of this hot path sees the mock-mode branch
  //      explicitly instead of it being buried behind a function call.
  //   2. Bundle size: fetchWithRetry runs in the client Apollo bundle, so a
  //      static import of the mock module would pull every fixture (~60 KB)
  //      into normal production JS. Gating a dynamic import on the
  //      build-time-inlined NEXT_PUBLIC_MOCK_API literal lets the bundler drop
  //      this whole branch — and the fixtures it references — from builds where
  //      the flag is unset. NEXT_PUBLIC_MOCK_API is set by every mock script
  //      alongside MOCK_API, and is also readable server-side.
  if (process.env.NEXT_PUBLIC_MOCK_API === 'true') {
    const { default: tryMockGraphQLResponse } = await import(
      '@/mocks/tryMockGraphQLResponse'
    );
    const mocked = tryMockGraphQLResponse(options?.body, {
      operationName: operationTag,
    });
    if (mocked) return mocked;
  }

  const { cache = 'force-cache', revalidate = 3600 } = options;
  const cacheStrategy = getCacheStrategy(options, cache);
  const fetchInit = { ...options, cache: cacheStrategy, revalidate };

  const tracer = trace.getTracer('GraphQL request');

  const parsedBody = (() => {
    try {
      return options?.body ? JSON.parse(options.body) : null;
    } catch {
      return null;
    }
  })();
  const recordVariables = Array.isArray(parsedBody)
    ? parsedBody[0]?.variables
    : parsedBody?.variables;

  return await tracer.startActiveSpan(debugLabel, async span => {
    const runFetch = async () => {
      const response = await fetch(url, fetchInit);
      // Recording mode (`yarn dev-record`) is server-only — recordFixture
      // writes fixtures to disk with node:fs. The `typeof window` guard is a
      // build-time constant the client bundler folds to `false`, so this
      // branch (and the recorder it imports) never reaches the browser bundle.
      if (typeof window === 'undefined' && process.env.MOCK_RECORD === 'true') {
        const { default: recordFixture } = await import(
          '@/mocks/recordFixture'
        );
        await recordFixture(operationTag, recordVariables, response);
      }
      return response;
    };

    const handleFailure = (causeErr, attemptIndex, didRetry) => {
      const error = new Error(`fetchWithRetry: Request error: ${debugLabel}`, {
        cause: causeErr,
      });
      logFetchFailure(error, url, causeErr, bodyMeta, {
        retry_attempt: attemptIndex,
        did_retry: didRetry,
      });
      span.recordException(error);
      span.setStatus({ code: 2, message: error.message });
      throw error;
    };

    try {
      try {
        return await runFetch();
      } catch (err) {
        if (err.name === 'AbortError') {
          span.addEvent('Request aborted');
          return null;
        }

        const allowRetry = isQueryOnly && isRetriableTransportCause(err);

        if (allowRetry) {
          try {
            return await runFetch();
          } catch (err2) {
            if (err2.name === 'AbortError') {
              span.addEvent('Request aborted');
              return null;
            }
            handleFailure(err2, 1, true);
          }
        }

        handleFailure(err, 0, false);
      }
    } finally {
      span.end();
    }
  });
};
